/**
 * Authentication store: a React context holding the signed-in user (or lack
 * thereof) and the actions from `docs/AUTH.md`.
 *
 * Chosen over Zustand (see `docs/FRONTEND.md`): the auth state is small,
 * changes rarely, and is read by very few components directly (mostly the
 * route guards) — React Context avoids an extra dependency for a state
 * shape this simple, while Zustand would pay off once more, higher-churn
 * global state appears in a later lot.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  fetchCsrfToken,
  getMe,
  logout as apiLogout,
  patchMe,
  requestMagicLink as apiRequestMagicLink,
  setUnauthorizedHandler,
  verifyMagicLink as apiVerifyMagicLink,
} from '../api/client';
import { isProfileComplete } from '../api/types';
import type { ProfileUpdate, User } from '../api/types';

/** The auth store's lifecycle: unresolved, then either signed out or in. */
export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: User | undefined;
  /** `true` once `user` has the fields `/onboarding` requires. */
  profileComplete: boolean;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<User>;
  updateProfile: (update: ProfileUpdate) => Promise<User>;
  /** Re-fetch `auth/me/`, e.g. to check whether an admin has just validated the account. */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provide the auth store to the app. Must render inside a Router, since it
 * uses `useNavigate` to send the volunteer back to `/login` when a session
 * expires (an API call answers `401`).
 *
 * @param props - Provider children.
 * @returns The provider element.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(undefined);
      setStatus('anonymous');
      navigate('/login', { replace: true });
    });
    return () => setUnauthorizedHandler(undefined);
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      await fetchCsrfToken().catch(() => undefined);
      try {
        const me = await getMe();
        if (!cancelled) {
          setUser(me);
          setStatus('authenticated');
        }
      } catch {
        // This probe started before any session existed, so a 403 only means
        // "there was none at mount time". A magic-link verification running in
        // parallel may have signed the volunteer in since — never downgrade a
        // session this probe simply predates, or opening a magic link becomes a
        // race between the two calls (which it was: `/auth/verify` succeeded,
        // then this reset the app to anonymous and bounced back to /login).
        if (!cancelled) {
          setStatus((current) => (current === 'loading' ? 'anonymous' : current));
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const requestMagicLink = useCallback(async (email: string) => {
    await apiRequestMagicLink(email);
  }, []);

  const verifyMagicLink = useCallback(async (token: string) => {
    const me = await apiVerifyMagicLink(token);
    setUser(me);
    setStatus('authenticated');
    return me;
  }, []);

  const updateProfile = useCallback(async (update: ProfileUpdate) => {
    const me = await patchMe(update);
    setUser(me);
    return me;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me);
      setStatus('authenticated');
    } catch {
      setUser(undefined);
      setStatus('anonymous');
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(undefined);
    setStatus('anonymous');
    navigate('/login', { replace: true });
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      profileComplete: user ? isProfileComplete(user) : false,
      requestMagicLink,
      verifyMagicLink,
      updateProfile,
      refresh,
      logout,
    }),
    [status, user, requestMagicLink, verifyMagicLink, updateProfile, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Access the auth store from a descendant of {@link AuthProvider}.
 *
 * @returns The current auth status, user and actions.
 * @throws {Error} When called outside an {@link AuthProvider}.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
